import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Play,
  Pause,
  SkipForward,
  Flag,
  RotateCcw,
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Loader2,
  Timer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PageContainer } from '@/components/layout/PageContainer';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { env } from '@/config/env';

export const Route = createFileRoute('/dashboard/simulation/')({
  component: SimulationPage,
});

// ─── Types ───────────────────────────────────────────────────────────────────

interface SimulatedPlayer {
  playerId: string;
  playerName: string;
  teamId: string;
  position: string;
}

interface SimulationState {
  matchId: string;
  leagueId: string;
  sportType: 'nfl' | 'nba' | 'mlb';
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamAbbreviation: string;
  awayTeamAbbreviation: string;
  scheduledDate: string;
  status: 'scheduled' | 'live' | 'finished';
  homeScore: number;
  awayScore: number;
  clock: string;
  quarter: number;
  totalPeriods: number;
  currentMinute: number;
  players: SimulatedPlayer[];
  playerStats: Record<string, Record<string, number>>;
  teamStats: Record<string, Record<string, number>>;
  intervalSeconds: number;
  isRunning: boolean;
  createdAt: string;
}

interface CreateSimulationForm {
  matchId: string;
  leagueId: string;
  sportType: 'nfl' | 'nba' | 'mlb';
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamAbbreviation: string;
  awayTeamAbbreviation: string;
  intervalSeconds: number;
  players: SimulatedPlayer[];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FEED_SERVICE_URL = import.meta.env.VITE_FEED_SERVICE_URL || 'http://localhost:5000';

const LEAGUE_OPTIONS = [
  { value: 'nfl', label: 'NFL', sport: 'nfl' as const },
  { value: 'nba', label: 'NBA', sport: 'nba' as const },
  { value: 'mlb', label: 'MLB', sport: 'mlb' as const },
];

const DEFAULT_FORM: CreateSimulationForm = {
  matchId: '',
  leagueId: '',
  sportType: 'nfl',
  homeTeamId: '',
  awayTeamId: '',
  homeTeamName: '',
  awayTeamName: '',
  homeTeamAbbreviation: '',
  awayTeamAbbreviation: '',
  intervalSeconds: 60,
  players: [],
};

const QUICK_FILL_DATA: CreateSimulationForm = {
  matchId: 'mock-match-chiefs-bills-11111111',
  leagueId: 'nfl',
  sportType: 'nfl',
  homeTeamId: 'mock-team-chiefs-11111111',
  awayTeamId: 'mock-team-bills-22222222',
  homeTeamName: 'Kansas City Chiefs',
  awayTeamName: 'Buffalo Bills',
  homeTeamAbbreviation: 'KC',
  awayTeamAbbreviation: 'BUF',
  intervalSeconds: 10,
  players: [
    {
      playerId: 'mock-player-mahomes-11111111',
      playerName: 'Patrick Mahomes',
      teamId: 'mock-team-chiefs-11111111',
      position: 'QB',
    },
    {
      playerId: 'mock-player-kelce-22222222',
      playerName: 'Travis Kelce',
      teamId: 'mock-team-chiefs-11111111',
      position: 'TE',
    },
    {
      playerId: 'mock-player-allen-44444444',
      playerName: 'Josh Allen',
      teamId: 'mock-team-bills-22222222',
      position: 'QB',
    },
  ],
};

// ─── API helpers ─────────────────────────────────────────────────────────────

async function fetchSimulations(): Promise<SimulationState[]> {
  const res = await fetch(`${FEED_SERVICE_URL}/simulation/matches`);
  if (!res.ok) throw new Error(`Failed to fetch simulations: ${res.statusText}`);
  return res.json();
}

async function createSimulation(data: CreateSimulationForm): Promise<SimulationState> {
  const res = await fetch(`${FEED_SERVICE_URL}/simulation/matches`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to create simulation: ${res.statusText}`);
  }
  return res.json();
}

async function controlSimulation(matchId: string, action: string): Promise<SimulationState> {
  const res = await fetch(`${FEED_SERVICE_URL}/simulation/matches/${matchId}/${action}`, {
    method: 'POST',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Action "${action}" failed: ${res.statusText}`);
  }
  return res.json();
}

async function deleteSimulation(matchId: string): Promise<void> {
  const res = await fetch(`${FEED_SERVICE_URL}/simulation/matches/${matchId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Failed to delete simulation: ${res.statusText}`);
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatusBadge({
  status,
  isRunning,
}: {
  status: SimulationState['status'];
  isRunning: boolean;
}) {
  if (status === 'live') {
    return (
      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-full bg-green-500 animate-pulse" />
        Live
        {isRunning && <span className="text-xs opacity-75">(running)</span>}
      </Badge>
    );
  }
  if (status === 'finished') {
    return (
      <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
        Finished
      </Badge>
    );
  }
  return (
    <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
      Scheduled
    </Badge>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className="ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function PlayerStatsTable({
  players,
  playerStats,
}: {
  players: SimulatedPlayer[];
  playerStats: Record<string, Record<string, number>>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-muted-foreground"
        onClick={() => setOpen(prev => !prev)}
      >
        {open ? (
          <ChevronDown className="h-3 w-3 mr-1" />
        ) : (
          <ChevronRight className="h-3 w-3 mr-1" />
        )}
        Player Stats ({players.length})
      </Button>
      {open && (
        <div className="mt-2 border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-gray-50 dark:bg-gray-900 text-xs py-2">Player</TableHead>
                <TableHead className="bg-gray-50 dark:bg-gray-900 text-xs py-2">Team</TableHead>
                <TableHead className="bg-gray-50 dark:bg-gray-900 text-xs py-2">Pos</TableHead>
                <TableHead className="bg-gray-50 dark:bg-gray-900 text-xs py-2">Stats</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {players.map(player => {
                const stats = playerStats[player.playerId] ?? {};
                const statEntries = Object.entries(stats);
                return (
                  <TableRow key={player.playerId}>
                    <TableCell className="text-xs py-2 font-medium">{player.playerName}</TableCell>
                    <TableCell className="text-xs py-2 text-muted-foreground font-mono">
                      {player.teamId.split('-').pop()}
                    </TableCell>
                    <TableCell className="text-xs py-2">
                      <Badge variant="outline" className="text-xs px-1 py-0">
                        {player.position}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs py-2">
                      {statEntries.length === 0 ? (
                        <span className="text-muted-foreground italic">no stats yet</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {statEntries.map(([k, v]) => (
                            <span
                              key={k}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-xs"
                            >
                              <span className="text-muted-foreground">{k}:</span>
                              <span className="font-medium">{v}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function SimulationCard({ sim }: { sim: SimulationState }) {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['simulations'] });

  const controlMutation = useMutation({
    mutationFn: (action: string) => controlSimulation(sim.matchId, action),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSimulation(sim.matchId),
    onSuccess: () => {
      setDeleteDialogOpen(false);
      invalidate();
    },
  });

  const totalMinutes =
    sim.sportType === 'nfl'
      ? sim.totalPeriods * 15
      : sim.sportType === 'nba'
        ? sim.totalPeriods * 12
        : sim.totalPeriods * 9;

  const isBusy = controlMutation.isPending || deleteMutation.isPending;
  const isFinished = sim.status === 'finished';

  return (
    <>
      <Card className="border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg">
                {sim.homeTeamAbbreviation} vs {sim.awayTeamAbbreviation}
              </CardTitle>
              <StatusBadge status={sim.status} isRunning={sim.isRunning} />
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="font-mono">{sim.matchId}</span>
              <CopyButton value={sim.matchId} />
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Score display */}
          <div className="flex items-center justify-center gap-4 py-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-1">{sim.homeTeamName}</div>
              <div className="text-4xl font-bold tabular-nums">{sim.homeScore}</div>
            </div>
            <div className="text-2xl text-muted-foreground font-light">–</div>
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-1">{sim.awayTeamName}</div>
              <div className="text-4xl font-bold tabular-nums">{sim.awayScore}</div>
            </div>
          </div>

          {/* Game info */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
            <span>
              <span className="font-medium text-foreground">Q{sim.quarter}</span> of{' '}
              {sim.totalPeriods}
            </span>
            <span>
              Clock: <span className="font-medium text-foreground font-mono">{sim.clock}</span>
            </span>
            <span>
              Minute:{' '}
              <span className="font-medium text-foreground">
                {sim.currentMinute}/{totalMinutes}
              </span>
            </span>
            <span>
              Interval: <span className="font-medium text-foreground">{sim.intervalSeconds}s</span>
            </span>
          </div>

          {/* Error display */}
          {controlMutation.isError && (
            <Alert variant="destructive" className="py-2">
              <AlertDescription className="text-xs">
                {(controlMutation.error as Error).message}
              </AlertDescription>
            </Alert>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="default"
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={isBusy || (sim.status === 'live' && sim.isRunning) || isFinished}
              onClick={() => controlMutation.mutate('start')}
            >
              {controlMutation.isPending && controlMutation.variables === 'start' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Play className="h-3.5 w-3.5 mr-1" />
              )}
              Start
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="border-yellow-500 text-yellow-700 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-950"
              disabled={isBusy || !sim.isRunning}
              onClick={() => controlMutation.mutate('pause')}
            >
              {controlMutation.isPending && controlMutation.variables === 'pause' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Pause className="h-3.5 w-3.5 mr-1" />
              )}
              Pause
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="border-blue-500 text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
              disabled={isBusy || isFinished}
              onClick={() => controlMutation.mutate('advance')}
            >
              {controlMutation.isPending && controlMutation.variables === 'advance' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <SkipForward className="h-3.5 w-3.5 mr-1" />
              )}
              Step +1
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="border-orange-500 text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-950"
              disabled={isBusy || isFinished}
              onClick={() => controlMutation.mutate('finish')}
            >
              {controlMutation.isPending && controlMutation.variables === 'finish' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Flag className="h-3.5 w-3.5 mr-1" />
              )}
              Finish
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="border-gray-400 text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-900"
              disabled={isBusy}
              onClick={() => controlMutation.mutate('reset')}
            >
              {controlMutation.isPending && controlMutation.variables === 'reset' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
              )}
              Reset
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="border-red-500 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 ml-auto"
              disabled={isBusy}
              onClick={() => setDeleteDialogOpen(true)}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5 mr-1" />
              )}
              Delete
            </Button>
          </div>

          {/* Player stats (collapsible) */}
          {sim.players.length > 0 && (
            <PlayerStatsTable players={sim.players} playerStats={sim.playerStats} />
          )}
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete simulation?</DialogTitle>
            <DialogDescription>
              This will permanently remove the simulation for{' '}
              <strong>
                {sim.homeTeamName} vs {sim.awayTeamName}
              </strong>{' '}
              (match ID: <code className="text-xs">{sim.matchId}</code>). This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Simulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PlayerRow({
  player,
  index,
  onChange,
  onRemove,
}: {
  player: SimulatedPlayer;
  index: number;
  onChange: (index: number, field: keyof SimulatedPlayer, value: string) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="flex gap-2 items-start p-3 border rounded-lg bg-gray-50 dark:bg-gray-900">
      <div className="grid grid-cols-2 gap-2 flex-1 sm:grid-cols-4">
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Player ID</Label>
          <Input
            value={player.playerId}
            onChange={e => onChange(index, 'playerId', e.target.value)}
            placeholder="mock-player-..."
            className="h-8 text-xs font-mono"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Name</Label>
          <Input
            value={player.playerName}
            onChange={e => onChange(index, 'playerName', e.target.value)}
            placeholder="Player Name"
            className="h-8 text-xs"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Team ID</Label>
          <Input
            value={player.teamId}
            onChange={e => onChange(index, 'teamId', e.target.value)}
            placeholder="mock-team-..."
            className="h-8 text-xs font-mono"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Position</Label>
          <Input
            value={player.position}
            onChange={e => onChange(index, 'position', e.target.value)}
            placeholder="QB"
            className="h-8 text-xs"
            maxLength={5}
          />
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 mt-5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 shrink-0"
        onClick={() => onRemove(index)}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function CreateSimulationPanel({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CreateSimulationForm>(DEFAULT_FORM);
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: createSimulation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simulations'] });
      setForm(DEFAULT_FORM);
      setOpen(false);
      onCreated();
    },
  });

  const handleLeagueChange = (leagueId: string) => {
    const league = LEAGUE_OPTIONS.find(l => l.value === leagueId);
    setForm(prev => ({
      ...prev,
      leagueId,
      sportType: league?.sport ?? prev.sportType,
    }));
  };

  const handlePlayerChange = (index: number, field: keyof SimulatedPlayer, value: string) => {
    setForm(prev => {
      const players = [...prev.players];
      players[index] = { ...players[index], [field]: value };
      return { ...prev, players };
    });
  };

  const addPlayer = () => {
    setForm(prev => ({
      ...prev,
      players: [...prev.players, { playerId: '', playerName: '', teamId: '', position: '' }],
    }));
  };

  const removePlayer = (index: number) => {
    setForm(prev => ({
      ...prev,
      players: prev.players.filter((_, i) => i !== index),
    }));
  };

  const handleQuickFill = () => {
    setForm(QUICK_FILL_DATA);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(form);
  };

  return (
    <Card className="border-gray-200 dark:border-gray-700">
      <CardHeader
        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 rounded-t-lg transition-colors pb-4"
        onClick={() => setOpen(prev => !prev)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Plus className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-base">Create New Simulation</CardTitle>
              <CardDescription className="text-sm mt-0.5">
                Set up a new simulated match for testing
              </CardDescription>
            </div>
          </div>
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </CardHeader>

      {open && (
        <CardContent className="pt-0">
          <div className="border-t dark:border-gray-700 pt-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Quick Fill button */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Fill in the fields below manually or use Quick Fill for Chiefs vs Bills (NFL).
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleQuickFill}
                  className="border-blue-400 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950 shrink-0"
                >
                  Quick Fill (Chiefs vs Bills)
                </Button>
              </div>

              {/* Match & League */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="matchId">Match ID</Label>
                  <Input
                    id="matchId"
                    value={form.matchId}
                    onChange={e => setForm(prev => ({ ...prev, matchId: e.target.value }))}
                    placeholder="mock-match-chiefs-bills-11111111"
                    className="font-mono text-sm"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    e.g. mock-match-chiefs-bills-11111111
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="leagueId">League</Label>
                  <Select value={form.leagueId} onValueChange={handleLeagueChange} required>
                    <SelectTrigger id="leagueId">
                      <SelectValue placeholder="Select league..." />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAGUE_OPTIONS.map(l => (
                        <SelectItem key={l.value} value={l.value}>
                          {l.label} —{' '}
                          <span className="font-mono text-xs text-muted-foreground">{l.value}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="sportType">Sport Type</Label>
                  <Input
                    id="sportType"
                    value={form.sportType}
                    readOnly
                    className="bg-muted text-muted-foreground cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground">
                    Auto-populated from league selection
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="intervalSeconds">Interval (seconds)</Label>
                  <Input
                    id="intervalSeconds"
                    type="number"
                    min={1}
                    max={300}
                    value={form.intervalSeconds}
                    onChange={e =>
                      setForm(prev => ({ ...prev, intervalSeconds: Number(e.target.value) }))
                    }
                    required
                  />
                  <p className="text-xs text-muted-foreground">Auto-advance interval, 1–300s</p>
                </div>
              </div>

              {/* Teams */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Teams</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="homeTeamId">Home Team ID</Label>
                    <Input
                      id="homeTeamId"
                      value={form.homeTeamId}
                      onChange={e => setForm(prev => ({ ...prev, homeTeamId: e.target.value }))}
                      placeholder="mock-team-chiefs-11111111"
                      className="font-mono text-sm"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="awayTeamId">Away Team ID</Label>
                    <Input
                      id="awayTeamId"
                      value={form.awayTeamId}
                      onChange={e => setForm(prev => ({ ...prev, awayTeamId: e.target.value }))}
                      placeholder="mock-team-bills-22222222"
                      className="font-mono text-sm"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="homeTeamName">Home Team Name</Label>
                    <Input
                      id="homeTeamName"
                      value={form.homeTeamName}
                      onChange={e => setForm(prev => ({ ...prev, homeTeamName: e.target.value }))}
                      placeholder="Kansas City Chiefs"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="awayTeamName">Away Team Name</Label>
                    <Input
                      id="awayTeamName"
                      value={form.awayTeamName}
                      onChange={e => setForm(prev => ({ ...prev, awayTeamName: e.target.value }))}
                      placeholder="Buffalo Bills"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="homeTeamAbbreviation">Home Abbreviation</Label>
                    <Input
                      id="homeTeamAbbreviation"
                      value={form.homeTeamAbbreviation}
                      onChange={e =>
                        setForm(prev => ({ ...prev, homeTeamAbbreviation: e.target.value }))
                      }
                      placeholder="KC"
                      maxLength={5}
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="awayTeamAbbreviation">Away Abbreviation</Label>
                    <Input
                      id="awayTeamAbbreviation"
                      value={form.awayTeamAbbreviation}
                      onChange={e =>
                        setForm(prev => ({ ...prev, awayTeamAbbreviation: e.target.value }))
                      }
                      placeholder="BUF"
                      maxLength={5}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Players */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold">Players ({form.players.length})</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addPlayer}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add Player
                  </Button>
                </div>

                {form.players.length === 0 ? (
                  <div className="text-center py-6 text-sm text-muted-foreground border border-dashed rounded-lg">
                    No players added yet. Use Quick Fill or add players manually.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {form.players.map((player, index) => (
                      <PlayerRow
                        key={index}
                        player={player}
                        index={index}
                        onChange={handlePlayerChange}
                        onRemove={removePlayer}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              {createMutation.isError && (
                <Alert variant="destructive">
                  <AlertDescription>{(createMutation.error as Error).message}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end">
                <Button type="submit" disabled={createMutation.isPending} className="min-w-32">
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Simulation
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function SimulationPage() {
  const {
    data: simulations = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['simulations'],
    queryFn: fetchSimulations,
    refetchInterval: 5000,
    retry: false,
  });

  if (!env.ENABLE_DEV_TOOLS) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>Dev Tools are not enabled for this environment.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <PageContainer>
      <Breadcrumbs overrides={{ simulation: 'Simulation' }} />

      {/* Page header */}
      <div className="flex items-start gap-4 mb-6 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <Timer className="h-6 w-6 text-muted-foreground shrink-0" />
            <h1 className="text-2xl font-semibold">Match Simulation</h1>
            <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 text-xs shrink-0">
              Mock Feed Only
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground ml-9">
            Control live match simulations for testing pickem scoring. Requires{' '}
            <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
              FEED_PROVIDER=mock
            </code>{' '}
            on the feed service.
          </p>
        </div>

        <Button variant="outline" size="sm" onClick={() => refetch()} className="shrink-0">
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Refresh
        </Button>
      </div>

      <div className="space-y-6">
        {/* Create simulation panel */}
        <CreateSimulationPanel onCreated={() => {}} />

        {/* Active simulations */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-lg font-semibold">Active Simulations</h2>
            {!isLoading && (
              <Badge variant="outline" className="text-xs">
                {simulations.length} match{simulations.length !== 1 ? 'es' : ''}
              </Badge>
            )}
            {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>

          {isError ? (
            <Card className="border-red-200 dark:border-red-800">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                    <Timer className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="font-medium text-red-700 dark:text-red-400">
                      Unable to reach feed service
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{(error as Error).message}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Make sure the feed service is running at{' '}
                      <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                        {FEED_SERVICE_URL}
                      </code>{' '}
                      with{' '}
                      <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                        FEED_PROVIDER=mock
                      </code>
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    Retry
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : isLoading ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {[1, 2].map(i => (
                <Card key={i} className="border-gray-200 dark:border-gray-700">
                  <CardHeader className="pb-3">
                    <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="h-20 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
                      <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(j => (
                          <div
                            key={j}
                            className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
                          />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : simulations.length === 0 ? (
            <Card className="border-dashed border-gray-300 dark:border-gray-700">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <Timer className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="font-medium text-muted-foreground">No simulations running</p>
                  <p className="text-sm text-muted-foreground">
                    Create a new simulation above to get started.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {simulations.map(sim => (
                <SimulationCard key={sim.matchId} sim={sim} />
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
